import { useMemo } from 'react';
import cx from 'classnames';

// @ts-expect-error - no TypeScript definitions yet
import { DocumentScoreBlock } from 'src/components/DocumentScoreBlock';
import { type DocumentVariation, variationHasDocumentScore } from 'src/data/document';
import { createStateSelectorHook } from 'src/lib/sweetState';
import {
  useDocumentEditorVariations,
  useDocumentEditorVariationsBoundaries,
  // @ts-expect-error - no TypeScript definitions yet
} from 'src/services/document';

import { useActiveVariationInstanceId } from './services/useActiveVariationInstanceId';
import { VariationDetails } from './views/VariationDetails';
import type { FetchDocumentVariationDetails } from './views/VariationDetails/useDetailsData';
import styles from './ReadonlyDocumentScores.module.scss';
import { ReadonlyDocumentScoresStore, useReadonlyDocumentScoresActions } from './store';

const useStoreState = createStateSelectorHook(ReadonlyDocumentScoresStore, [
  'detailsPanelVisibleForInstanceId',
]);

interface ReadonlyDocumentScoresProps {
  className?: string;
  // The element the editor's saved items are measured in.
  parentElement: HTMLElement;
  // Where the details panel opens.
  portalElement: HTMLElement | null;
  benchmarkProjectId: string;
  fetchDetails: FetchDocumentVariationDetails;
}

/** The scores of a read-only document's saved items, and their details panel. */
export const ReadonlyDocumentScores = ({
  className,
  parentElement,
  portalElement,
  benchmarkProjectId,
  fetchDetails,
}: ReadonlyDocumentScoresProps) => {
  const actions = useReadonlyDocumentScoresActions();
  const activeInstanceId = useActiveVariationInstanceId();
  const { detailsPanelVisibleForInstanceId } = useStoreState();

  const variations: DocumentVariation[] = useDocumentEditorVariations(null);
  const scoredVariations = useMemo(
    () => variations.filter(variationHasDocumentScore),
    [variations]
  );
  const boundaries = useDocumentEditorVariationsBoundaries({
    parentElement,
    activeInstanceId,
    lockedScoresShown: false,
  });
  const detailsVariation = useMemo(
    () => variations.find(({ instanceId }) => instanceId === detailsPanelVisibleForInstanceId),
    [variations, detailsPanelVisibleForInstanceId]
  );

  return (
    <div className={cx(styles.root, className)}>
      {scoredVariations.map((variation) => (
        <DocumentScoreBlock
          key={variation.instanceId}
          variation={variation}
          boundaries={boundaries[variation.instanceId]}
          onDetailsClick={() => actions.openVariationDetailsPanel(variation.instanceId)}
          onHover={() => actions.activateVariation(variation.instanceId)}
          expanded={variation.instanceId === activeInstanceId}
        />
      ))}

      {detailsVariation && portalElement && (
        <VariationDetails
          variation={detailsVariation}
          portalElement={portalElement}
          benchmarkProjectId={benchmarkProjectId}
          fetchDetails={fetchDetails}
        />
      )}
    </div>
  );
};
